var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'goober';
import { FaArrowLeft, FaArrowRight, FaChevronDown } from 'react-icons/fa';
import FSButton from './FSButton';
var PickerInput = styled('input', forwardRef)(function (_a) {
    var theme = _a.theme, $size = _a.$size;
    return ({
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: theme.colors.neutral.w100,
        borderRadius: theme.borderRadius,
        backgroundColor: theme.colors.neutral.w50,
        color: theme.colors.ui.dark,
        padding: $size === 'normal' ? '8px' : '4px 8px',
        width: $size === 'normal' ? '100%' : '70px',
    });
});
var PickerContainer = styled('div', forwardRef)(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'fixed',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.borderRadius,
        boxShadow: "0px 4px 16px ".concat(theme.boxShadow),
        padding: '8px',
        gap: '8px',
        backgroundColor: theme.colors.ui.white,
        color: theme.colors.ui.dark,
    });
});
var PickerYearRow = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        fontWeight: 'bold',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    });
});
var PickerYearRowItem = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '4px',
    });
});
var PickerMonthGrid = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: '8px',
    });
});
var PickerMonthGridItem = styled('div')(function (_a) {
    var theme = _a.theme, $selected = _a.$selected, $disabled = _a.$disabled;
    var style = {
        borderRadius: theme.borderRadius,
        cursor: 'pointer',
        userSelect: 'none',
        color: theme.colors.ui.dark,
        fontWeight: 'normal',
        padding: '4px 8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };
    if ($disabled) {
        style.opacity = 0.5;
        style.cursor = 'not-allowed';
    }
    else if ($selected) {
        style.backgroundColor = theme.colors.primary.w50;
        style.color = theme.colors.primary.w500;
    }
    else {
        style['&:hover'] = { backgroundColor: theme.colors.neutral.w100 };
    }
    return style;
});
var YearSubmenuContainer = styled('div', forwardRef)(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'fixed',
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.borderRadius,
        boxShadow: "0px 4px 16px ".concat(theme.boxShadow),
        padding: '4px',
        gap: '4px',
        backgroundColor: theme.colors.ui.white,
        color: theme.colors.ui.dark,
        fontWeight: 'normal',
    });
});
function zeroFillMonth(month) {
    return "00".concat(month).substr(-2);
}
export default function FSMonthPicker(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var selectedMonth = _a.selectedMonth, selectedYear = _a.selectedYear, onChange = _a.onChange, years = _a.years, yearMinProp = _a.yearMin, yearMaxProp = _a.yearMax, _l = _a.disabledMonthsByYear, disabledMonthsByYear = _l === void 0 ? {} : _l, _m = _a.size, size = _m === void 0 ? 'normal' : _m;
    var _o = __read(useState(false), 2), menuShown = _o[0], setMenuShown = _o[1];
    var _p = __read(useState(false), 2), yearSubmenuShown = _p[0], setYearSubmenuShown = _p[1];
    var _q = __read(useState(''), 2), inputValue = _q[0], setInputValue = _q[1];
    var _r = __read(useState(selectedYear), 2), tempYear = _r[0], setTempYear = _r[1];
    var inputRef = useRef(null);
    var inputBoundingRect = (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
    var pickerRef = useRef(null);
    var yearSubmenuToggleRef = useRef(null);
    var yearSubmenuToggleBoundingRect = (_c = yearSubmenuToggleRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
    var yearSubmenuRef = useRef(null);
    var yearMin = years !== undefined ? Math.min.apply(Math, __spreadArray([], __read(years), false)) : yearMinProp !== null && yearMinProp !== void 0 ? yearMinProp : 1970;
    var yearMax = years !== undefined ? Math.max.apply(Math, __spreadArray([], __read(years), false)) : yearMaxProp !== null && yearMaxProp !== void 0 ? yearMaxProp : 9999;
    useEffect(function () {
        // reset input text if selection has changed
        setInputValue("".concat(zeroFillMonth(selectedMonth), "/").concat(selectedYear));
    }, [selectedMonth, selectedYear]);
    useEffect(function () {
        setTempYear(selectedYear);
    }, [selectedYear]);
    useEffect(function () {
        if (!menuShown) {
            setYearSubmenuShown(false);
        }
    }, [menuShown]);
    useEffect(function () {
        // close on outside click
        var handleClick = function (e) {
            var _a, _b, _c;
            if ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
                return;
            }
            if ((_b = pickerRef.current) === null || _b === void 0 ? void 0 : _b.contains(e.target)) {
                return;
            }
            if ((_c = yearSubmenuRef.current) === null || _c === void 0 ? void 0 : _c.contains(e.target)) {
                return;
            }
            setInputValue("".concat(zeroFillMonth(selectedMonth), "/").concat(selectedYear));
            setMenuShown(false);
        };
        // add eventlistener to close element on outside click
        document.addEventListener('mousedown', handleClick);
        return function () {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [inputRef, pickerRef, selectedMonth, selectedYear]);
    function textInputUpdate() {
        var _a = __read(inputValue.split('/', 2), 2), newMonthString = _a[0], newYearString = _a[1];
        var monthNumber = Number.parseInt(newMonthString);
        var monthSafe = 1;
        if (!Number.isNaN(monthNumber)) {
            if (monthNumber < 1) {
                monthSafe = 1;
            }
            else if (monthNumber > 12) {
                monthSafe = 12;
            }
            else {
                monthSafe = monthNumber;
            }
        }
        var yearNumber = Number.parseInt(newYearString);
        var yearSafe = yearMin;
        if (!Number.isNaN(yearNumber)) {
            if (yearNumber < yearMin) {
                yearSafe = yearMin;
            }
            else if (yearNumber > yearMax) {
                yearSafe = yearMax;
            }
            else {
                yearSafe = yearNumber;
            }
        }
        onChange(monthSafe, yearSafe);
    }
    var monthGridItems = [];
    var _loop_1 = function (month) {
        var thisMonthDisabled = !!((_d = disabledMonthsByYear[tempYear]) === null || _d === void 0 ? void 0 : _d.includes(month));
        monthGridItems.push(React.createElement(PickerMonthGridItem, { key: "month-".concat(month), onClick: thisMonthDisabled
                ? undefined
                : function () {
                    onChange(month, tempYear);
                    setMenuShown(false);
                }, "$selected": tempYear === selectedYear && selectedMonth === month, "$disabled": thisMonthDisabled }, zeroFillMonth(month)));
    };
    for (var month = 1; month <= 12; month++) {
        _loop_1(month);
    }
    var pickerComponent = (React.createElement(PickerContainer, { ref: pickerRef, style: {
            visibility: menuShown ? 'visible' : 'hidden',
            top: ((_e = inputBoundingRect === null || inputBoundingRect === void 0 ? void 0 : inputBoundingRect.y) !== null && _e !== void 0 ? _e : 0) + ((_f = inputBoundingRect === null || inputBoundingRect === void 0 ? void 0 : inputBoundingRect.height) !== null && _f !== void 0 ? _f : 0) + 4,
            left: menuShown ? (_g = inputBoundingRect === null || inputBoundingRect === void 0 ? void 0 : inputBoundingRect.x) !== null && _g !== void 0 ? _g : 0 : '-100%',
        } },
        React.createElement(PickerYearRow, null,
            React.createElement(PickerYearRowItem, null,
                React.createElement("span", { style: { width: '30px' } }, tempYear),
                years !== undefined && (React.createElement(FSButton, { ref: yearSubmenuToggleRef, icon: FaChevronDown, "$type": "tertiary", "$color": "neutral", "$size": "small", onClick: function () { return setYearSubmenuShown(!yearSubmenuShown); } }))),
            React.createElement(PickerYearRowItem, null,
                React.createElement(FSButton, { icon: FaArrowLeft, "$type": "tertiary", "$color": "neutral", "$size": "small", onClick: function () { return setTempYear(tempYear - 1); }, disabled: tempYear <= yearMin }),
                React.createElement(FSButton, { icon: FaArrowRight, "$type": "tertiary", "$color": "neutral", "$size": "small", onClick: function () { return setTempYear(tempYear + 1); }, disabled: tempYear >= yearMax }))),
        React.createElement(PickerMonthGrid, null, monthGridItems)));
    var yearSubmenuComponent = years !== undefined ? (React.createElement(YearSubmenuContainer, { ref: yearSubmenuRef, style: {
            visibility: yearSubmenuShown ? 'visible' : 'hidden',
            top: ((_h = yearSubmenuToggleBoundingRect === null || yearSubmenuToggleBoundingRect === void 0 ? void 0 : yearSubmenuToggleBoundingRect.y) !== null && _h !== void 0 ? _h : 0) + ((_j = yearSubmenuToggleBoundingRect === null || yearSubmenuToggleBoundingRect === void 0 ? void 0 : yearSubmenuToggleBoundingRect.height) !== null && _j !== void 0 ? _j : 0) + 4,
            left: yearSubmenuShown ? (_k = yearSubmenuToggleBoundingRect === null || yearSubmenuToggleBoundingRect === void 0 ? void 0 : yearSubmenuToggleBoundingRect.x) !== null && _k !== void 0 ? _k : 0 : '-100%',
        } }, years.map(function (y) { return (React.createElement(FSButton, { key: "year-".concat(y), title: String(y), "$type": "tertiary", "$color": y === tempYear ? 'primary' : 'neutral', onClick: function () {
            setYearSubmenuShown(false);
            setTempYear(y);
        } })); }))) : null;
    return (React.createElement(React.Fragment, null,
        React.createElement(PickerInput, { type: "text", ref: inputRef, value: inputValue, "$size": size, onChange: function (e) { return setInputValue(e.target.value); }, onFocus: function () { return setMenuShown(true); }, onKeyDown: function (e) {
                if (e.key === 'Enter') {
                    textInputUpdate();
                }
            } }),
        createPortal(pickerComponent, document.getElementById('portal')),
        createPortal(yearSubmenuComponent, document.getElementById('portal'))));
}
